import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { useAppSelector } from "../../redux";

const margin = { top: 25, right: 35, bottom: 70, left: 70 },
  width = 480,
  height = 400,
  radius =
    Math.min(width, height) / 2 - Math.max(...Object.values(margin)) + 10;

const FuelPieChart = ({ scale }) => {
  const oldGraphData = useAppSelector((state) => state.fuel).data;

  const ref1 = useRef();
  const ref2 = useRef();

  const styles = {
    transform: `scale(${scale})`,
    display: "flex",
    justifyContent: "space-around",
  };

  useEffect(() => {
    if (oldGraphData.length === 0) return;

    //  make renewables category
    const renewableTypes = ["Solar", "Refuse", "Wood", "Wind", "Landfill Gas"];
    const renewableSum = oldGraphData
      .filter((d) => renewableTypes.includes(d.type))
      .reduce((acc, cur) => acc + cur.amount, 0);

    const graphData = oldGraphData
      .filter((d) => !renewableTypes.includes(d.type))
      .concat({ type: "Renewables", amount: renewableSum });

    // Color scale
    const color = d3
      .scaleOrdinal()
      .domain(graphData.map((d) => d.type))
      .range(["#5EC3E7", "#6285FF", "#B5E175", "#FFAB2A", "#46A37D"]);

    // data for "Resources" chart
    const resourcesData = graphData.filter((d) =>
      ["Natural Gas", "Nuclear", "Hydro", "Renewables", "Other"].includes(
        d.type
      )
    );

    // data for "Renewables" chart
    const renewablesData = oldGraphData.filter((d) =>
      ["Solar", "Refuse", "Wood", "Wind", "Landfill Gas"].includes(d.type)
    );

    const drawPieChart = (ref, data, title) => {
      const svgContainer = d3.select(ref.current);
      svgContainer.selectAll("*").remove();

      const svg = svgContainer
        .append("svg")
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", `0 0 ${width} ${height}`)
        .attr("style", "max-width: 100%; height: auto; font: 10px National2;")
        .append("g")
        .attr("transform", `translate(${width / 2}, ${height / 3})`);

      const totalAmount = data.reduce((acc, cur) => acc + cur.amount, 0);

      const pie = d3
        .pie()
        .value((d) => d.amount)
        .sort(null);

      const arc = d3.arc().innerRadius(80).outerRadius(radius);

      const arcs = svg
        .selectAll(".arc")
        .data(pie(data))
        .enter()
        .append("g")
        .attr("class", "arc");

      arcs
        .append("path")
        .attr("d", arc)
        .attr("fill", (d) => color(d.data.type));

      // title
      svg
        .append("text")
        .attr("x", 0)
        .attr("y", -radius + 320)
        .attr("text-anchor", "middle")
        .style("font-size", "30px")
        .style("fill", "#333")
        .text(title);

      // Legend
      const legend = svg
        .selectAll(".legend")
        .data(pie(data))
        .enter()
        .append("g")
        .attr("class", "legend")
        .attr("transform", (d, i) => {
          const x = -radius + (i % 2) * 220;
          const y = radius + 65 + Math.floor(i / 2) * 42;
          return `translate(${x}, ${y})`;
        });

      legend
        .append("circle")
        .attr("cx", 10)
        .attr("cy", 20)
        .attr("r", 12)
        .style("fill", (d) => color(d.data.type));

      legend
        .append("text")
        .attr("x", 30)
        .attr("y", 27)
        .style("font-size", "20px")
        .text((d) => d.data.type);

      legend
        .append("text")
        .attr("x", -10)
        .attr("y", 27)
        .style("font-size", "20px")
        .style("fill", "#474747")
        .style("font-weight", "500")
        .style("text-anchor", "end")
        .text((d) =>
          (d.data.amount / totalAmount) * 100 >= 1
            ? `${((d.data.amount / totalAmount) * 100).toFixed(0)}%`
            : "<1%"
        );
    };

    drawPieChart(ref1, resourcesData, "Resources");
    drawPieChart(ref2, renewablesData, "Renewables");
  }, [oldGraphData]);

  return (
    <div style={styles}>
      <div>
        <svg ref={ref1} width={width} height={height} />
      </div>
      <div>
        <svg ref={ref2} width={width} height={height} />
      </div>
    </div>
  );
};

export default FuelPieChart;
